import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, KeyboardEvent, MouseEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  helpHrefForPath,
  initialsFromName,
  serviceIsActive,
  sessionRoleLabel,
  visibleServices,
  type ServiceDefinition,
} from '@inventohub/shared';
import { useAuth } from '../lib/auth';
import { useScanQr } from '../lib/scan-qr';
import { SearchOverlay } from './SearchOverlay';

export function AppChrome(): JSX.Element {
  const { user, code, loading, logout } = useAuth();
  const signedIn = Boolean(user || code);
  const location = useLocation();
  const navigate = useNavigate();
  const { openScan } = useScanQr();
  const [appMenuOpen, setAppMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  const services = useMemo(() => visibleServices(user, code), [user, code]);
  const displayName = user?.name ?? code?.displayName ?? '';
  const initials = initialsFromName(displayName);
  const roleLabel = sessionRoleLabel(user, code);

  useEffect(() => {
    function onKey(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape') {
        setAppMenuOpen(false);
        setAccountOpen(false);
        return;
      }
      if (event.key !== '/') return;
      if (!signedIn || searchOpen) return;
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return;
        }
      }
      event.preventDefault();
      setSearchOpen(true);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [signedIn, searchOpen]);

  function onHeaderKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key === 'Escape') {
      setAppMenuOpen(false);
      setAccountOpen(false);
    }
  }

  function activateService(service: ServiceDefinition): void {
    setAppMenuOpen(false);
    if (service.action === 'scan-qr') {
      openScan();
      return;
    }
    void navigate(service.href);
  }

  return (
    <>
      <header className="app__header" ref={headerRef} onKeyDown={onHeaderKeyDown}>
        <button
          type="button"
          className="app__icon-btn"
          aria-label="Open app menu"
          aria-expanded={appMenuOpen}
          aria-controls="app-menu"
          data-testid="app-menu-button"
          onClick={() => {
            setAppMenuOpen((open) => !open);
            setAccountOpen(false);
          }}
        >
          ☰
        </button>

        <div className="app__brand">
          <Link
            to="/"
            className="app__wordmark-link"
            data-testid="app-wordmark-link"
            onClick={() => {
              setAppMenuOpen(false);
              setAccountOpen(false);
            }}
          >
            <img
              src="/brand/ewp-wordmark-on-light.png"
              alt="Elite Wrestling Productions"
              className="app__wordmark app__wordmark--on-light"
              data-testid="ewp-wordmark-on-light"
            />
            <img
              src="/brand/ewp-wordmark-on-dark.png"
              alt=""
              className="app__wordmark app__wordmark--on-dark"
              data-testid="ewp-wordmark-on-dark"
              aria-hidden="true"
            />
          </Link>
          <Link
            to="/"
            className="app__title-link"
            data-testid="app-title-link"
            onClick={() => {
              setAppMenuOpen(false);
              setAccountOpen(false);
            }}
          >
            <h1 className="app__title" data-testid="app-title">
              InventoHub
            </h1>
          </Link>
        </div>

        <div className="app__header-actions">
          {signedIn ? (
            <>
              <button
                type="button"
                className="app__search-field"
                data-testid="search-field"
                onClick={() => {
                  setSearchOpen(true);
                  setAppMenuOpen(false);
                  setAccountOpen(false);
                }}
              >
                Search events, assets, totes, services
              </button>
              <button
                type="button"
                className="app__icon-btn app__search-icon"
                aria-label="Search"
                data-testid="search-button"
                onClick={() => {
                  setSearchOpen(true);
                  setAppMenuOpen(false);
                  setAccountOpen(false);
                }}
              >
                🔍
              </button>
              <Link
                to={helpHrefForPath(location.pathname)}
                className="app__icon-btn"
                aria-label="Help"
                data-testid="help-button"
              >
                ?
              </Link>
              <button
                type="button"
                className="app__icon-btn app__account"
                aria-label="Account menu"
                aria-expanded={accountOpen}
                aria-controls="account-menu"
                data-testid="account-menu-button"
                onClick={() => {
                  setAccountOpen((open) => !open);
                  setAppMenuOpen(false);
                }}
              >
                {initials}
              </button>
            </>
          ) : (
            <Link to="/login" className="app__sign-in" data-testid="sign-in-control">
              Sign in
            </Link>
          )}
        </div>

        {appMenuOpen ? (
          <AppMenu
            signedIn={signedIn}
            loading={loading}
            pathname={location.pathname}
            services={services}
            onClose={() => setAppMenuOpen(false)}
            onService={activateService}
          />
        ) : null}

        {accountOpen && signedIn ? (
          <AccountMenu
            displayName={displayName}
            roleLabel={roleLabel}
            onLogout={() => {
              setAccountOpen(false);
              void logout();
            }}
            onClose={() => setAccountOpen(false)}
          />
        ) : null}
      </header>
      {searchOpen && signedIn ? (
        <SearchOverlay user={user} code={code} onClose={() => setSearchOpen(false)} />
      ) : null}
    </>
  );
}

function AppMenu({
  signedIn,
  loading,
  pathname,
  services,
  onClose,
  onService,
}: {
  signedIn: boolean;
  loading: boolean;
  pathname: string;
  services: ServiceDefinition[];
  onClose: () => void;
  onService: (service: ServiceDefinition) => void;
}): JSX.Element {
  return (
    <>
      <button
        type="button"
        className="flyout__backdrop"
        aria-label="Close menu"
        onClick={onClose}
      />
      <nav id="app-menu" className="flyout flyout--app" aria-label="App" data-testid="app-menu">
        <NavLink to="/" end className="flyout__item" onClick={onClose} data-testid="app-menu-home">
          Home
        </NavLink>
        {signedIn
          ? services.map((service) => {
              const current = serviceIsActive(pathname, service);
              if (service.action === 'scan-qr') {
                return (
                  <button
                    key={service.id}
                    type="button"
                    className="flyout__item"
                    data-testid={`app-menu-${service.id}`}
                    onClick={() => onService(service)}
                  >
                    {service.label}
                  </button>
                );
              }
              return (
                <NavLink
                  key={service.id}
                  to={service.href}
                  className="flyout__item"
                  aria-current={current ? 'page' : undefined}
                  data-testid={`app-menu-${service.id}`}
                  onClick={onClose}
                >
                  {service.label}
                </NavLink>
              );
            })
          : null}
        {!loading && !signedIn ? (
          <NavLink
            to="/login"
            className="flyout__item"
            onClick={onClose}
            data-testid="app-menu-sign-in"
          >
            Sign in
          </NavLink>
        ) : null}
      </nav>
    </>
  );
}

function AccountMenu({
  displayName,
  roleLabel,
  onLogout,
  onClose,
}: {
  displayName: string;
  roleLabel: string;
  onLogout: () => void;
  onClose: () => void;
}): JSX.Element {
  function stop(event: MouseEvent): void {
    event.stopPropagation();
  }
  return (
    <>
      <button
        type="button"
        className="flyout__backdrop"
        aria-label="Close account menu"
        onClick={onClose}
      />
      <div
        id="account-menu"
        className="flyout flyout--account"
        role="menu"
        data-testid="account-menu"
        onClick={stop}
      >
        <p className="flyout__name" data-testid="account-name">
          {displayName}
        </p>
        <p className="meta" data-testid="account-role">
          {roleLabel}
        </p>
        <NavLink
          to="/diagnostics"
          className="flyout__item"
          role="menuitem"
          onClick={onClose}
          data-testid="account-diagnostics"
        >
          Diagnostics
        </NavLink>
        <button
          type="button"
          className="flyout__item flyout__item--danger"
          role="menuitem"
          onClick={onLogout}
          data-testid="logout-button"
        >
          Log out
        </button>
      </div>
    </>
  );
}
